"use client";

import Image from "next/image";
import AnimatedContent from "../AnimatedContent";
import { useTranslation } from "react-i18next";
import SectionContainer from "@/common/SectionContainer";

interface PlanItem {
  title: string;
  description: string;
}

const MarketSection = () => {
  const { t } = useTranslation();
  const b2bPlans = t("MARKET_SECTION_B2B_PLANS", {
    returnObjects: true,
  }) as PlanItem[];
  const b2cPlans = t("MARKET_SECTION_B2C_PLANS", {
    returnObjects: true,
  }) as PlanItem[];

  return (
    <SectionContainer
      id="market-section"
      className="section-shell bg-blue-50 text-gray-900"
    >
      <AnimatedContent className="section-inner section-stack">
        <div className="section-head">
          <h2 className="section-title">{t("MARKET_SECTION_TITLE")}</h2>
          <p className="caption text-gray-600 section-lead whitespace-pre-wrap">
            {t("MARKET_SECTION_SUB_TITLE")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div className="flex flex-col bg-white rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden">
            <div className="relative w-full aspect-[16/9]">
              <Image
                src="https://zipshow-static-web.s3.ap-northeast-2.amazonaws.com/homepage/color-to-life-biz/main_background.png"
                alt={t("MARKET_SECTION_B2B_IMAGE_ALT")}
                fill
                className="object-cover"
                sizes="(max-width: 768px) 100vw, 50vw"
                quality={90}
              />
              <div className="absolute top-0 left-0 w-full bg-gradient-to-r from-black/60 to-transparent text-white p-4 text-[clamp(1rem,2vw,1.25rem)] font-medium">
                {t("MARKET_SECTION_B2B_IMAGE_LABEL")}
              </div>
            </div>

            <div className="p-6 sm:p-8 flex flex-col gap-4 flex-1">
              <p className="text-gray-500 text-[clamp(0.9rem,1.3vw,1rem)] font-medium">
                <strong>{t("MARKET_SECTION_B2B_MODEL_TITLE")}</strong>
                {" "}
                {t("MARKET_SECTION_B2B_MODEL_SUB")}
              </p>

              <div className="space-y-4 border-t border-gray-200 pt-4">
                {b2bPlans.map((plan, i) => (
                  <div
                    key={plan.title}
                    className="flex flex-col sm:flex-row sm:items-start"
                  >
                    <span className="md:w-1/4 text-[#207AE8] font-semibold text-[clamp(0.95rem,1.3vw,1.1rem)]">
                      {String(i + 1).padStart(2, "0")} {plan.title}
                    </span>
                    <p className="md:w-3/4 text-gray-700 text-[clamp(0.9rem,1.2vw,1rem)]">
                      {plan.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col bg-white rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden">
            <div className="relative w-full aspect-[16/9]">
              <Image
                src="https://zipshow-static-web.s3.ap-northeast-2.amazonaws.com/homepage/color-to-life-biz/color_to_life_main.png"
                sizes="(max-width: 768px) 100vw, 50vw"
                alt={t("MARKET_SECTION_B2C_IMAGE_ALT")}
                fill
                className="object-cover"
                quality={90}
              />
              <div className="absolute top-0 left-0 w-full bg-gradient-to-r from-black/60 to-transparent text-white p-4 text-[clamp(1rem,2vw,1.25rem)] font-medium">
                {t("MARKET_SECTION_B2C_IMAGE_LABEL")}
              </div>
            </div>

            <div className="p-6 sm:p-8 flex flex-col gap-4 flex-1">
              <p className="text-gray-500 text-[clamp(0.9rem,1.3vw,1rem)] font-medium">
                <strong>{t("MARKET_SECTION_B2C_MODEL_TITLE")}</strong>
                {" "}
                {t("MARKET_SECTION_B2C_MODEL_SUB")}
              </p>

              <div className="space-y-4 border-t border-gray-200 pt-4">
                {b2cPlans.map((plan) => (
                  <div
                    key={plan.title}
                    className="flex flex-col sm:flex-row sm:items-start"
                  >
                    <span className="text-[#207AE8] font-semibold text-[clamp(0.95rem,1.3vw,1.1rem)] md:w-1/4">
                      {plan.title}
                    </span>
                    <p className="md:w-3/4 text-gray-700 text-[clamp(0.9rem,1.2vw,1rem)]">
                      {plan.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </AnimatedContent>
    </SectionContainer>
  );
};

export default MarketSection;
